import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Modal,
  Button,
  Form,
  Alert,
  Spinner,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { createWriteOff } from "../../afinanceapi";
import AppToast from "../Toasts/AppToast";
import { useToast } from "../Toasts/useToast";
import "./acc-list.css";

/**
 * WriteOffModal — confirms a full-balance write-off for an account.
 *
 * Visual conventions: see /CLAUDE.md "frontend design system" §1.8 Modals
 * (header tint, h6 title, footer order) and §1.4 Buttons (`danger` is the
 * confirmed-destructive variant). Close prompt only fires when the note
 * field is dirty so empty closes don't re-prompt.
 */
export default function WriteOffModal({ show, onHide, account }) {
  const navigate = useNavigate();
  const [writeOffNote, setWriteOffNote] = useState("");
  const [writeOffDate, setWriteOffDate] = useState(
    () => new Date().toISOString().split("T")[0],
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast, showToast, hideToast } = useToast();
  const [closeAlert, setCloseAlert] = useState(false);
  const [hiddenForSuccess, setHiddenForSuccess] = useState(false);

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      await createWriteOff({
        accountId: account._id,
        writeOffDate,
        writeOffNote,
        writeOffPracticeNumber: "",
      });

      showToast("success", "Write-off created successfully!");
      setHiddenForSuccess(true);
      setTimeout(() => {
        onHide();
        navigate(0);
      }, 2500);
    } catch (error) {
      console.error("Failed to create write-off:", error);
      showToast("error", "Failed to create write-off. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  function handleClose() {
    if (writeOffNote.trim()) {
      setCloseAlert(true);
    } else {
      onHide();
    }
  }

  const headerName = account
    ? [account.accountFullNames, account.accountLastName]
        .filter(Boolean)
        .join(" ")
        .trim() ||
      account.accountNumber ||
      ""
    : "";

  return (
    <>
      <Modal
        show={show && !hiddenForSuccess}
        onHide={handleClose}
        centered
        size="lg"
        scrollable
      >
        <Modal.Header
          closeButton={!closeAlert}
          style={{ background: "#CFE2FF" }}
          className="rounded-0 text-primary d-flex align-items-center"
        >
          <Modal.Title as="h6" className="m-0">
            Write Off Account{headerName ? ` — ${headerName}` : ""}
          </Modal.Title>
        </Modal.Header>

        {closeAlert && (
          <Alert
            variant="info"
            className="w-100 d-flex align-items-center justify-content-between mx-0 mb-0 border-0 rounded-0 small"
          >
            <span>
              Are you sure you want to close? Changes will not be saved.
            </span>
            <div className="ms-auto d-flex gap-2">
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Discard and close</Tooltip>}
              >
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    setCloseAlert(false);
                    onHide();
                  }}
                  aria-label="Discard and close"
                >
                  <i className="fa-solid fa-check" />
                </Button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Keep editing</Tooltip>}
              >
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setCloseAlert(false)}
                  aria-label="Keep editing"
                >
                  <i className="fa-solid fa-xmark" />
                </Button>
              </OverlayTrigger>
            </div>
          </Alert>
        )}

        <Modal.Body>
          <Alert variant="warning" className="mb-3">
            This will write off the <strong>full outstanding balance</strong>{" "}
            for this account. All invoices with outstanding amounts will be
            marked as <strong>Written Off</strong>.
          </Alert>

          <Form.Group className="mb-3">
            <Form.Label>Write Off Date</Form.Label>
            <Form.Control
              type="date"
              value={writeOffDate}
              onChange={(e) => setWriteOffDate(e.target.value)}
              style={{ maxWidth: 220 }}
            />
          </Form.Group>

          <Form.Group className="mb-0">
            <Form.Label>Note</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Enter a reason for the write-off…"
              value={writeOffNote}
              onChange={(e) => setWriteOffNote(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="danger"
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{ minWidth: 140 }}
          >
            {isSubmitting ? (
              <Spinner animation="border" size="sm" role="status" />
            ) : (
              <>
                <i className="fa-solid fa-ban me-2" />
                Write Off
              </>
            )}
          </Button>
          <Button
            variant="outline-secondary"
            onClick={handleClose}
            disabled={isSubmitting}
            style={{ minWidth: 140 }}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
      <AppToast
        show={toast.show}
        onClose={hideToast}
        variant={toast.variant}
        message={toast.message}
      />
    </>
  );
}
